import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { changePassword, listActiveSessions, listSecurityEvents, logoutAllDevices, logoutUser, revokeActiveSession } from '../authService';
import { useAuth } from '../context/AuthContext';
import { listConversationThreads } from '../lib/conversationThreads';
import { CelestialShell, SystemFooter } from './ui/CelestialShell';

function formatAccountDate(value, fallback = 'Not available') {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(parsed);
}

function formatAccountDateTime(value, fallback = 'Active now') {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(parsed);
}

function DetailRow({ label, value, muted = false }) {
  return (
    <div className="br-profile-detail">
      <dt>{label}</dt>
      <dd className={muted ? 'is-muted' : ''}>{value}</dd>
    </div>
  );
}

export default function Profile() {
  const { authState, setAuthState } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [activityAvailable, setActivityAvailable] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [signingOutEverywhere, setSigningOutEverywhere] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [securityMessage, setSecurityMessage] = useState('');

  const account = authState?.user || {};
  const username = String(account.username || account.sub || '').trim();
  const displayName = String(account.display_name || account.name || username || 'Your account').trim();
  const email = String(account.email || '').trim();

  useEffect(() => {
    let active = true;
    listConversationThreads()
      .then((body) => {
        if (!active) return;
        setThreads(Array.isArray(body?.threads) ? body.threads : []);
        setActivityAvailable(true);
      })
      .catch(() => {
        if (!active) return;
        setThreads([]);
        setActivityAvailable(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    Promise.all([listActiveSessions(), listSecurityEvents()])
      .then(([sessionBody, eventBody]) => {
        setSessions(Array.isArray(sessionBody?.sessions) ? sessionBody.sessions : []);
        setSecurityEvents(Array.isArray(eventBody?.events) ? eventBody.events : []);
      })
      .catch(() => setSecurityMessage('Security activity is temporarily unavailable.'));
  }, []);

  const latestConversation = useMemo(() => {
    const valid = threads
      .map((thread) => thread?.updated_at || thread?.created_at)
      .filter(Boolean)
      .map((value) => new Date(value))
      .filter((value) => !Number.isNaN(value.getTime()));
    if (!valid.length) return null;
    return new Date(Math.max(...valid.map((value) => value.getTime())));
  }, [threads]);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    await logoutUser();
    setAuthState((current) => ({
      ...current, isAuthenticated: false, user: null, preferences: null,
    }));
    navigate('/login', { replace: true });
  };

  const handleSignOutEverywhere = async () => {
    if (signingOut || signingOutEverywhere) return;
    setSigningOutEverywhere(true);
    const revoked = await logoutAllDevices();
    if (!revoked) {
      setSigningOutEverywhere(false);
      return;
    }
    setAuthState((current) => ({
      ...current, isAuthenticated: false, user: null, preferences: null,
    }));
    navigate('/login', { replace: true });
  };

  const handleRevokeSession = async (sessionId) => {
    await revokeActiveSession(sessionId);
    setSessions((current) => current.filter((session) => session.id !== sessionId));
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setSecurityMessage('');
    try {
      await changePassword(currentPassword, newPassword);
      setSecurityMessage('Password changed. Sign in again with your new password.');
      setAuthState((current) => ({ ...current, isAuthenticated: false, user: null, preferences: null }));
      window.setTimeout(() => navigate('/login', { replace: true }), 900);
    } catch (error) {
      setSecurityMessage(error?.message === 'current_password_invalid' ? 'Current password is incorrect.' : 'Password could not be changed.');
    }
  };

  return (
    <CelestialShell authenticated className="br-profile-page">
      <main className="br-container br-profile-layout">
        <header className="br-profile-hero br-glass" aria-labelledby="profile-title">
          <div className="br-profile-identity">
            <div className="br-profile-primary">
              <p className="br-eyebrow">Your account</p>
              <h1 id="profile-title">{displayName}</h1>
              {username && displayName.toLowerCase() !== username.toLowerCase() && (
                <p className="br-profile-handle">@{username}</p>
              )}
            </div>
            <dl className="br-profile-identity-strip" aria-label="Account identity">
              {username && displayName.toLowerCase() !== username.toLowerCase() && <div><dt>Username</dt><dd>@{username}</dd></div>}
              {email && <div><dt>Email</dt><dd>{email}</dd></div>}
              {account.created_at && <div><dt>Joined</dt><dd>{formatAccountDate(account.created_at)}</dd></div>}
              <div><dt>Account status</dt><dd><span className="br-profile-status-dot" aria-hidden="true" />Signed in</dd></div>
            </dl>
          </div>
        </header>

        <div className="br-profile-panels">
          <section className="br-profile-panel br-profile-panel--security br-glass" aria-labelledby="profile-security-heading">
            <div className="br-profile-panel__heading">
              <p className="br-eyebrow">Access &amp; security</p>
              <h2 id="profile-security-heading">Access and security</h2>
            </div>
            <dl className="br-profile-details">
              <DetailRow label="Current access" value={formatAccountDateTime(account.signed_in_at)} />
            </dl>
            <div className="br-security-sessions" aria-label="Signed-in devices">
              <h3>Signed-in devices</h3>
              {sessions.length === 0 && <p className="br-section__copy">No device details are available right now.</p>}
              {sessions.map((session) => <div className="br-security-session" key={session.id}>
                <div><strong>{session.device}</strong><span>{session.current ? 'Current device' : formatAccountDate(new Date(session.last_seen_at * 1000), 'Recently active')}</span></div>
                {!session.current && <button type="button" className="br-button br-button--quiet" onClick={() => handleRevokeSession(session.id)}>Sign out device</button>}
              </div>)}
            </div>
            <form className="br-password-form" onSubmit={handlePasswordChange}>
              <h3>Change password</h3>
              <input className="br-input" type="password" autoComplete="current-password" aria-label="Current password for change" placeholder="Current password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
              <input className="br-input" type="password" autoComplete="new-password" minLength="12" aria-label="New password" placeholder="New password, 12 characters minimum" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
              <button className="br-button" type="submit" disabled={!currentPassword || newPassword.length < 12}>Update password</button>
            </form>
            <div className="br-security-events" aria-label="Recent security activity">
              <h3>Recent security activity</h3>
              {securityEvents.slice(0, 6).map((event, index) => <div key={`${event.created_at}-${index}`}><strong>{String(event.event || '').replaceAll('_', ' ')}</strong><span>{formatAccountDate(new Date(event.created_at * 1000))}</span></div>)}
            </div>
            {securityMessage && <p role="status" className="br-status">{securityMessage}</p>}
            <div className="br-profile-actions">
              <Link className="br-button" to="/settings">Account settings</Link>
              <button className="br-button br-button--quiet" type="button" onClick={handleSignOut} disabled={signingOut}>
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
              <button className="br-button br-button--quiet" type="button" onClick={handleSignOutEverywhere} disabled={signingOut || signingOutEverywhere}>
                {signingOutEverywhere ? 'Signing out everywhere…' : 'Sign out everywhere'}
              </button>
            </div>
          </section>

          <section className="br-profile-panel br-profile-panel--activity br-glass" aria-labelledby="profile-activity-heading">
            <div className="br-profile-panel__heading">
              <p className="br-eyebrow">Account activity</p>
              <h2 id="profile-activity-heading">Your continuity</h2>
            </div>
            <dl className="br-profile-details br-profile-details--activity">
              <DetailRow
                label="Most recent conversation"
                value={activityAvailable ? formatAccountDate(latestConversation, 'No saved conversations yet') : 'Temporarily unavailable'}
                muted={!activityAvailable || !latestConversation}
              />
              <DetailRow
                label="Saved conversations"
                value={activityAvailable ? String(threads.length) : 'Temporarily unavailable'}
                muted={!activityAvailable}
              />
              {account.created_at && <DetailRow label="Account created" value={formatAccountDate(account.created_at)} />}
            </dl>
            <nav className="br-profile-destinations" aria-label="Profile destinations">
              <Link to="/settings#conversation">
                <span><strong>Conversation preferences</strong><small>Memory, Insights, and continuity</small></span>
                <span aria-hidden="true">↗</span>
              </Link>
              <Link to="/settings#privacy-data">
                <span><strong>Privacy and data</strong><small>Export, retention, and deletion</small></span>
                <span aria-hidden="true">↗</span>
              </Link>
              <a href="#profile-security-heading">
                <span><strong>Security controls</strong><small>Password and signed-in devices</small></span>
                <span aria-hidden="true">↑</span>
              </a>
            </nav>
          </section>
        </div>

        <SystemFooter />
      </main>
    </CelestialShell>
  );
}
